import {
  TypeHeroToast,
  TOAST_PRIORITY,
  TOAST_DURATION,
  UNDO_DURATION,
  MAX_VISIBLE,
  DEDUPE_WINDOW_MS,
} from "./types";

export function formatAggregateTitle(baseTitle: string, count: number): string {
  if (count <= 1) return baseTitle;
  const clean = baseTitle.replace(/\s*x\d+$/, "").replace(/^\d+\s+/, "");
  return `${count} ${clean}`;
}

export function aggregateQueue(queue: TypeHeroToast[]): TypeHeroToast[] {
  const result: TypeHeroToast[] = [];
  const seen = new Map<string, number>();

  for (const toast of queue) {
    const key = toast._dedupeKey || `${toast.type}:${toast.title}`;
    const idx = seen.get(key);

    if (idx !== undefined) {
      result[idx] = {
        ...result[idx],
        _count: (result[idx]._count || 1) + 1,
      };
    } else {
      seen.set(key, result.length);
      result.push({ ...toast, _count: toast._count || 1 });
    }
  }

  return result;
}

interface MergeResult {
  updated: TypeHeroToast[];
  displacedIds: string[];
  resetTimerIds: string[];
  newToasts: TypeHeroToast[];
}

export function mergeIntoStack(
  current: TypeHeroToast[],
  incoming: TypeHeroToast[],
  now: number
): MergeResult {
  let updated = [...current];
  const displacedIds: string[] = [];
  const resetTimerIds: string[] = [];
  const newToasts: TypeHeroToast[] = [];

  for (const toast of incoming) {
    const dedupeKey = toast._dedupeKey || `${toast.type}:${toast.title}`;

    const existingIdx = updated.findIndex(
      (t) =>
        !t._exiting &&
        (t._dedupeKey || `${t.type}:${t.title}`) === dedupeKey &&
        now - t._createdAt < DEDUPE_WINDOW_MS
    );

    if (existingIdx >= 0) {
      const existing = updated[existingIdx];
      const newCount = (existing._count || 1) + (toast._count || 1);
      updated[existingIdx] = {
        ...existing,
        _count: newCount,
        title: formatAggregateTitle(toast.title, newCount),
        _createdAt: now,
      };
      resetTimerIds.push(existing.id);
      continue;
    }

    const visible = updated.filter((t) => !t._exiting);
    if (visible.length >= MAX_VISIBLE) {
      const incomingPriority = TOAST_PRIORITY[toast.type];
      const lowest = visible.reduce((a, b) =>
        TOAST_PRIORITY[a.type] < TOAST_PRIORITY[b.type] ? a : b
      );

      if (TOAST_PRIORITY[lowest.type] < incomingPriority) {
        displacedIds.push(lowest.id);
        updated = updated.filter((t) => t.id !== lowest.id);
      } else {
        continue;
      }
    }

    updated.push(toast);
    newToasts.push(toast);
  }

  updated.sort(
    (a, b) =>
      TOAST_PRIORITY[b.type] - TOAST_PRIORITY[a.type] ||
      a._createdAt - b._createdAt
  );

  return { updated, displacedIds, resetTimerIds, newToasts };
}

export function getAutoDismissDuration(toast: TypeHeroToast): number {
  if (toast.action?.label === 'Undo') return UNDO_DURATION;
  return TOAST_DURATION[toast.type];
}
